import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

// Tab button component
const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-xl font-medium transition-all ${
      active 
        ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20' 
        : 'bg-white/5 text-slate-400 hover:bg-white/10'
    }`}
  >
    {children}
  </button>
);

// Transaction item component
const TransactionItem = ({ tx }) => {
  const isCredit = tx.amount_cents > 0;
  const typeLabels = {
    tip: isCredit ? 'Tip Received' : 'Tip Sent',
    withdrawal: 'Withdrawal',
    deposit: 'Deposit',
    transfer: isCredit ? 'Received' : 'Sent',
    reward: 'Reward',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 rounded-xl bg-black/20 border border-white/5 flex items-center justify-between hover:bg-black/30 transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${
          isCredit ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'
        }`}>
          {isCredit ? '↓' : '↑'}
        </div>
        <div>
          <div className="font-medium text-white">
            {typeLabels[tx.type] || (isCredit ? 'Received' : 'Sent')}
          </div>
          <div className="text-xs text-slate-500">
            {new Date(tx.created_at).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
          {tx.metadata?.message && (
            <div className="text-xs text-slate-400 mt-1">"{tx.metadata.message}"</div>
          )}
        </div>
      </div>
      <div className={`font-bold text-lg ${isCredit ? 'text-emerald-400' : 'text-white'}`}>
        {isCredit ? '+' : ''}{(tx.amount_cents / 100).toFixed(2)}
      </div>
    </motion.div>
  );
};

// Quick tip preset button
const QuickTipButton = ({ amount, onClick, disabled }) => (
  <button
    onClick={() => onClick(amount)}
    disabled={disabled}
    className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 text-purple-300 hover:border-purple-500/50 hover:text-purple-200 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
  >
    ${(amount / 100).toFixed(0)}
  </button>
);

export default function Wallet() {
  const { user } = useAuth();
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Transfer state
  const [transferLoading, setTransferLoading] = useState(false);
  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [tipMessage, setTipMessage] = useState('');
  
  // Withdrawal state
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [paypalEmail, setPaypalEmail] = useState('');
  const [withdrawalLoading, setWithdrawalLoading] = useState(false);
  
  // Feedback state
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadWalletData = useCallback(async () => {
    try {
      setLoading(true);
      const [balanceData, txData] = await Promise.all([
        apiService.getWalletBalance(),
        apiService.getWalletTransactions(50, 0)
      ]);
      setBalance(balanceData.balance_cents || 0);
      setTransactions(txData || []);
    } catch (err) {
      console.error('Failed to load wallet data:', err);
      setError('Failed to load wallet information.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWalletData();
  }, [loadWalletData]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const handleTip = async (quickAmount = null) => {
    const tipAmount = quickAmount || Math.round(parseFloat(amount) * 100);
    
    if (!recipient) {
      setError('Please enter a recipient username');
      return;
    }
    if (!tipAmount || tipAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (tipAmount > balance) {
      setError('Insufficient funds');
      return;
    }

    setError(null);
    setSuccess(null);
    setTransferLoading(true);

    try {
      // Resolve username to ID
      let recipientId = recipient;
      if (isNaN(recipient)) {
        const profile = await apiService.getProfile(recipient.replace('@', ''));
        if (!profile?.id) {
          throw new Error('Recipient not found');
        }
        recipientId = profile.id;
      }

      await apiService.sendTip(recipientId, tipAmount, tipMessage || null);
      setSuccess(`Sent $${(tipAmount / 100).toFixed(2)} to ${recipient}!`);
      setAmount('');
      setRecipient('');
      setTipMessage('');
      loadWalletData();
    } catch (err) {
      console.error('Tip failed:', err);
      setError(err.message || 'Tip failed');
    } finally {
      setTransferLoading(false);
    }
  };

  const handleWithdrawal = async (e) => {
    e.preventDefault();
    const amountCents = Math.round(parseFloat(withdrawalAmount) * 100);

    if (amountCents < 1000) {
      setError('Minimum withdrawal is $10.00');
      return;
    }
    if (amountCents > balance) {
      setError('Insufficient funds');
      return;
    }
    if (paymentMethod === 'paypal' && !paypalEmail) {
      setError('Please enter your PayPal email');
      return;
    }

    setError(null);
    setSuccess(null);
    setWithdrawalLoading(true);

    try {
      const accountDetails = paymentMethod === 'paypal' 
        ? { email: paypalEmail }
        : {};

      await apiService.requestWithdrawal(amountCents, paymentMethod, accountDetails);
      setSuccess(`Withdrawal of $${(amountCents / 100).toFixed(2)} requested! Processing takes 1-3 business days.`);
      setWithdrawalAmount('');
      loadWalletData();
    } catch (err) {
      console.error('Withdrawal failed:', err);
      setError(err.message || 'Withdrawal failed');
    } finally {
      setWithdrawalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen px-6 text-center">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="rounded-2xl border border-white/10 bg-white/5 px-6 py-4 text-white/80"
        >
          <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto mb-3" />
          Loading wallet...
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 space-y-6">
      {/* Balance Card */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900 to-slate-800 p-8 text-center shadow-2xl"
      >
        <h1 className="text-xl font-bold text-white mb-2">Wallet Balance</h1>
        <div className="text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400 my-4">
          ${(balance / 100).toFixed(2)}
        </div>
        <p className="text-slate-400 text-sm">Available for tips & withdrawals</p>
      </motion.div>

      {/* Feedback Messages */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-4 rounded-xl bg-red-500/20 border border-red-500/40 text-red-200"
          >
            {error}
          </motion.div>
        )}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-4 rounded-xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-200"
          >
            {success}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tab Navigation */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
          Overview
        </TabButton>
        <TabButton active={activeTab === 'tip'} onClick={() => setActiveTab('tip')}>
          Send Tip
        </TabButton>
        <TabButton active={activeTab === 'withdraw'} onClick={() => setActiveTab('withdraw')}>
          Withdraw
        </TabButton>
        <TabButton active={activeTab === 'history'} onClick={() => setActiveTab('history')}>
          History
        </TabButton>
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'overview' && (
          <motion.div
            key="overview"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center">
              <div className="text-3xl font-bold text-emerald-400">
                ${((transactions.filter(t => t.amount_cents > 0).reduce((sum, t) => sum + t.amount_cents, 0)) / 100).toFixed(2)}
              </div>
              <div className="text-sm text-slate-400 mt-1">Total Received</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center">
              <div className="text-3xl font-bold text-rose-400">
                ${(Math.abs(transactions.filter(t => t.amount_cents < 0).reduce((sum, t) => sum + t.amount_cents, 0)) / 100).toFixed(2)}
              </div>
              <div className="text-sm text-slate-400 mt-1">Total Sent</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center">
              <div className="text-3xl font-bold text-cyan-400">
                {transactions.filter(t => t.type === 'tip').length}
              </div>
              <div className="text-sm text-slate-400 mt-1">Tips Given/Received</div>
            </div>
            
            {/* Recent Activity */}
            <div className="col-span-full mt-4">
              <h3 className="text-lg font-medium text-white mb-3">Recent Activity</h3>
              <div className="space-y-2">
                {transactions.slice(0, 5).map(tx => (
                  <TransactionItem key={tx.id} tx={tx} />
                ))}
                {transactions.length === 0 && (
                  <div className="text-center text-slate-500 py-8">
                    No transactions yet. Start by receiving tips or adding funds!
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'tip' && (
          <motion.div
            key="tip"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="rounded-2xl border border-white/10 bg-white/5 p-6"
          >
            <h2 className="text-xl font-bold text-white mb-6">Send a Tip</h2>
            
            {/* Quick tip amounts */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-400 mb-2">Quick Tip</label>
              <div className="flex flex-wrap gap-2">
                {[100, 200, 500, 1000, 2000].map(amt => (
                  <QuickTipButton 
                    key={amt} 
                    amount={amt} 
                    onClick={() => handleTip(amt)}
                    disabled={transferLoading || !recipient || amt > balance}
                  />
                ))}
              </div>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); handleTip(); }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Recipient</label>
                <input
                  type="text"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  placeholder="@username"
                  className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 transition"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Custom Amount ($)</label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  min="0.01"
                  step="0.01"
                  className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 transition"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Message (optional)</label>
                <input
                  type="text"
                  value={tipMessage}
                  onChange={(e) => setTipMessage(e.target.value)}
                  placeholder="Thanks for the great content!"
                  maxLength={200}
                  className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-white placeholder-slate-500 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 transition"
                />
              </div>

              <button
                type="submit"
                disabled={transferLoading || !recipient || !amount}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold hover:from-purple-500 hover:to-pink-500 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-lg shadow-purple-900/20"
              >
                {transferLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                    Sending...
                  </span>
                ) : (
                  `Send Tip${amount ? ` ($${parseFloat(amount).toFixed(2)})` : ''}`
                )}
              </button>
            </form>
          </motion.div>
        )}

        {activeTab === 'withdraw' && (
          <motion.div
            key="withdraw"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="rounded-2xl border border-white/10 bg-white/5 p-6"
          >
            <h2 className="text-xl font-bold text-white mb-6">Withdraw Funds</h2>
            
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6">
              <p className="text-yellow-200 text-sm">
                <strong>Minimum withdrawal:</strong> $10.00 • Processing time: 1-3 business days
              </p>
            </div>

            <form onSubmit={handleWithdrawal} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-2">Payment Method</label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('stripe')}
                    className={`flex-1 py-3 rounded-xl border transition-all ${
                      paymentMethod === 'stripe'
                        ? 'border-emerald-500 bg-emerald-500/20 text-white'
                        : 'border-white/10 bg-white/5 text-slate-400'
                    }`}
                  >
                    💳 Bank/Card (Stripe)
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('paypal')}
                    className={`flex-1 py-3 rounded-xl border transition-all ${
                      paymentMethod === 'paypal'
                        ? 'border-blue-500 bg-blue-500/20 text-white'
                        : 'border-white/10 bg-white/5 text-slate-400'
                    }`}
                  >
                    📧 PayPal
                  </button>
                </div>
              </div>

              {paymentMethod === 'paypal' && (
                <div>
                  <label className="block text-sm font-medium text-slate-400 mb-1">PayPal Email</label>
                  <input
                    type="email"
                    value={paypalEmail}
                    onChange={(e) => setPaypalEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Amount ($)</label>
                <input
                  type="number"
                  value={withdrawalAmount}
                  onChange={(e) => setWithdrawalAmount(e.target.value)}
                  placeholder="10.00"
                  min="10"
                  max={(balance / 100).toFixed(2)}
                  step="0.01"
                  className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 transition"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  Available: ${(balance / 100).toFixed(2)}
                </p>
              </div>

              <button
                type="submit"
                disabled={withdrawalLoading || !withdrawalAmount || parseFloat(withdrawalAmount) < 10}
                className="w-full py-3 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-lg shadow-emerald-900/20"
              >
                {withdrawalLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                    Processing...
                  </span>
                ) : (
                  `Withdraw $${withdrawalAmount || '0.00'}`
                )}
              </button>
            </form>
          </motion.div>
        )}

        {activeTab === 'history' && (
          <motion.div
            key="history"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="rounded-2xl border border-white/10 bg-white/5 p-6"
          >
            <h2 className="text-xl font-bold text-white mb-6">Transaction History</h2>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
              {transactions.length > 0 ? (
                transactions.map(tx => (
                  <TransactionItem key={tx.id} tx={tx} />
                ))
              ) : (
                <div className="text-center text-slate-500 py-10">
                  No transactions yet
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
